import {
  Checkbox,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TableSortLabel,
  TablePagination,
  Box,
  Button,
} from "@mui/material";
import { ReactNode, useMemo, useState } from "react";

// データ構造定義
export type TrustBoxItem = {
  id: string | number; // ID（非表示）
  parentId?: string; // 親の ID（子行のみ） （非表示）
  isParent?: boolean; // 親データ行かどうかフラグ
  image?: string; // リンク
  inputDate?: string; // 入力日
  user?: string; // ユーザ
  itemName?: string; // 商品名
  janCode?: string; // JAN
  expiryDate?: string; // 賞味期限
  setCount?: number; // セット数
  deliveryCount?: number; // 納品数
  deliveryUnit?: string;
  expectedCount?: number; // 予定入荷数
  shippedCount?: number; // 実出荷単位数
  receivedCount?: number; // 実入荷総数
  asin?: string;
  sku?: string;
  fnsku?: string;
  stickerNum?: number;
  returnNum?: number;
  destructionNum?: number;
  storageLocation?: string;
  baseId: number;
  userId: number;
  memo?: string;
  statusId?: number;
  rowColor?: "yellow" | "green" | "blue" | "gray" | "default";
  isChecked?: boolean;
};

type Header = {
  label: string;
  key: keyof TrustBoxItem;
  width?: number | string;
};

type Props = {
  data: TrustBoxItem[];
  totalListCount: number;
  checkedIds: Set<string>;
  onCheckedChange: (newChecked: Set<string>) => void;
  onParentRowClick?: (parent: TrustBoxItem, children: TrustBoxItem[]) => void;
  handleExpiryDatePrintAllAtOnce: () => void;
  handlePrintAllAtOnce: () => void;
  onSortChange?: (
    key: keyof TrustBoxItem | null,
    order: Order // 'asc' | 'desc'
  ) => void;
  onPageChange?: (page: number, limit: number) => void;
};

// ソート順の型
type Order = "asc" | "desc";

// 文字サイズ
const TABLE_HEADER_FONT_SIZE = "0.65rem";
const TABLE_BODY_FONT_SIZE = "1rem";

// ヘッダー定義
const headers: Header[] = [
  { label: "入力日", key: "inputDate", width: 60 },
  { label: "ユーザー", key: "user", width: 100 },
  { label: "商品名", key: "itemName", width: 200 },
  { label: "JAN", key: "janCode", width: 120 },
  { label: "賞味期限", key: "expiryDate", width: 120 },
  { label: "セット数", key: "setCount", width: 80 },
  { label: "納品数", key: "deliveryCount", width: 80 },
  { label: "予定入荷総数", key: "expectedCount", width: 100 },
  // { label: "実出荷数", key: "shippedCount", width: 100 },
  { label: "実入荷総数", key: "receivedCount", width: 100 },
  { label: "ASIN", key: "asin", width: 140 },
];

const TrustBoxTable = ({
  data,
  totalListCount,
  checkedIds,
  onCheckedChange,
  onParentRowClick,
  handleExpiryDatePrintAllAtOnce,
  handlePrintAllAtOnce,
  onSortChange,
  onPageChange,
}: Props) => {
  const [orderBy, setOrderBy] = useState<keyof TrustBoxItem | null>(null);
  const [order, setOrder] = useState<Order>("asc");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);

  // const [checkedIds, setCheckedIds] = useState<Set<string>>(new Set());

  // const selectedItems = data.filter((item) => checkedIds.has(String(item.id))); // チェックが入っているデータ

  // 親データと子単体データをまとめたグループデータ
  const groupedData = useMemo(() => {
    const parentMap = new Map<
      string,
      { parent: TrustBoxItem; children: TrustBoxItem[] }
    >();
    const orphanItems: TrustBoxItem[] = [];

    data.forEach((item) => {
      if (item.isParent) {
        parentMap.set(String(item.id), { parent: item, children: [] });
      } else if (item.parentId && parentMap.has(String(item.parentId))) {
        parentMap.get(String(item.parentId))!.children.push(item);
      } else {
        orphanItems.push(item); // 親がいないデータ
      }
    });

    return { parentMap, orphanItems };
  }, [data]);

  // 親データ、子単体でソート（子データは親データにひっつくようにしている）
  const sortedData: TrustBoxItem[] = useMemo(() => {
    const { parentMap, orphanItems } = groupedData;

    const result: TrustBoxItem[] = [];

    // 親と子を順に展開
    parentMap.forEach(({ parent, children }) => {
      result.push(parent, ...children);
    });

    // 孤立項目も追加
    result.push(...orphanItems);

    return result;
  }, [groupedData]);

  const parentRows = useMemo(
    () => sortedData.filter((r) => r.isParent),
    [sortedData]
  );

  const paginatedData = useMemo(() => {
    // 今ページに載せる親 id 一覧
    const pagedParentIds = parentRows.slice(0, rowsPerPage).map((p) => p.id);

    // 親行 + その子行 をまとめて返す
    return sortedData.filter(
      (row) =>
        row.isParent
          ? pagedParentIds.includes(row.id) // 親自身
          : pagedParentIds.includes(String(row.parentId)) // 子で、親が今ページに載るもの
    );
  }, [sortedData, parentRows, page, rowsPerPage]);

  // ヘッダーのソート
  const handleSort = (key: keyof TrustBoxItem) => {
    const isAsc = orderBy === key && order === "asc";
    const newOrder = isAsc ? "desc" : "asc";
    setOrderBy(key);
    setOrder(isAsc ? "desc" : "asc");
    setPage(0); // ← 並べ替え時は 1 ページ目に戻す
    onSortChange?.(key, newOrder as Order);
  };

  const handleParentCheck = (parentId: string, childIds: string[]) => {
    const newSet = new Set(checkedIds);
    if (newSet.has(parentId)) {
      newSet.delete(parentId);
      childIds.forEach((id) => newSet.delete(id));
    } else {
      newSet.add(parentId);
      childIds.forEach((id) => newSet.add(id));
    }
    onCheckedChange(newSet);
  };

  const handleSelectAll = () => {
    const allIds = data.flatMap((item) =>
      item.isParent
        ? [
            String(item.id),
            ...data
              .filter((c) => c.parentId === item.id)
              .map((c) => String(c.id)),
          ]
        : []
    );
    const newSet =
      checkedIds.size === allIds.length
        ? new Set<string>()
        : new Set<string>(allIds);

    onCheckedChange(newSet);
  };

  const hasChecked = checkedIds.size > 0;

  const isNotPrintExpiryDate = () => {
    const selectedItems = data.filter((item) =>
      checkedIds.has(String(item.id))
    );
    return selectedItems.every(
      (item) => !item.expiryDate || item.expiryDate.trim() === ""
    );
  };

  return (
    <>
      <TablePagination
        labelRowsPerPage="1ページの表示件数："
        component="div"
        rowsPerPageOptions={[5, 10, 25, 50]}
        count={totalListCount} // 全行数
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={(_, newPage) => {
          setPage(newPage);
          onPageChange?.(newPage, rowsPerPage);
        }}
        onRowsPerPageChange={(e) => {
          const newRowsPerPage = parseInt(e.target.value, 10);
          setRowsPerPage(parseInt(e.target.value, 10));
          setPage(0); // 行数変更時は 1 ページ目へ
          onPageChange?.(0, newRowsPerPage);
        }}
        labelDisplayedRows={({ from, to, count }) =>
          `${from}〜${to} 件／全 ${count} 件`
        }
      />
      <TableContainer
        component={Paper}
        sx={{ height: "calc(100vh - 180px)", overflowY: "auto" }}
      >
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox
                  checked={
                    checkedIds.size > 0 && checkedIds.size === data.length
                  }
                  indeterminate={
                    checkedIds.size > 0 && checkedIds.size < data.length
                  }
                  onChange={(e) => {
                    e.stopPropagation();
                    handleSelectAll();
                  }}
                />
              </TableCell>
              <TableCell
                sx={{
                  fontSize: TABLE_HEADER_FONT_SIZE,
                  px: 0,
                  width: 80,
                  textAlign: "center",
                }}
              >
                リンク
              </TableCell>
              {headers.map((header) => (
                <TableCell
                  key={String(header.key)}
                  sortDirection={orderBy === header.key ? order : false}
                  sx={{
                    fontSize: TABLE_HEADER_FONT_SIZE,
                    px: 0,
                    textAlign: "center",
                    width: header.width,
                  }}
                >
                  {header.key !== "janCode" && header.key !== "expiryDate" ? (
                    <TableSortLabel
                      active={orderBy === header.key}
                      direction={orderBy === header.key ? order : "asc"}
                      onClick={() => handleSort(header.key)}
                    >
                      {header.label}
                    </TableSortLabel>
                  ) : (
                    <TableSortLabel
                      active={orderBy === header.key}
                      direction={orderBy === header.key ? order : "asc"}
                      onClick={() => handleSort(header.key)}
                      disabled
                    >
                      {header.label}
                    </TableSortLabel>
                  )}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedData.map((item) => (
              <TableRow
                key={item.id}
                onClick={() => {
                  if (item.isParent && onParentRowClick) {
                    const children = data.filter((c) => c.parentId === item.id);
                    onParentRowClick(item, children);
                  }
                }}
                sx={{
                  height: item.isParent ? 80 : undefined,
                  cursor: item.isParent ? "pointer" : "default",
                  backgroundColor:
                    item.rowColor === "yellow"
                      ? "#fff59d"
                      : item.rowColor === "green"
                      ? "#81c784"
                      : item.rowColor === "blue"
                      ? "#64b5f6"
                      : item.rowColor === "gray"
                      ? "#e0e0e0"
                      : "inherit",
                  fontSize: TABLE_BODY_FONT_SIZE,
                }}
              >
                <TableCell
                  padding="checkbox"
                  sx={{
                    fontSize: TABLE_BODY_FONT_SIZE,
                    ...(item.isParent && { py: 10 }),
                  }}
                  onClick={(e) => e.stopPropagation()}
                >
                  {item.isParent && (
                    <Checkbox
                      checked={checkedIds.has(String(item.id))}
                      onChange={(e) => {
                        e.stopPropagation();
                        const childIds = data
                          .filter((c) => c.parentId === item.id)
                          .map((c) => String(c.id));
                        handleParentCheck(String(item.id), childIds);
                      }}
                    />
                  )}
                </TableCell>
                <TableCell onClick={(e) => e.stopPropagation()}>
                  {item.isParent && (
                    <a
                      href={`https://www.amazon.co.jp/dp/${item.asin}`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <img
                        src={
                          item.image ??
                          "https://via.placeholder.com/200x200?text=No+Image"
                        }
                        alt="no image"
                        style={{
                          width: 200,
                          height: 200,
                          objectFit: "contain",
                          cursor: "pointer",
                        }}
                      />
                    </a>
                  )}
                </TableCell>
                {headers.map((header) => {
                  const value = item[header.key];
                  const align = typeof value === "number" ? "right" : "left";

                  // JAN はクリック無効
                  const isDisabledClickColumn =
                    header.key === "janCode" || header.key === "asin";

                  return (
                    <TableCell
                      key={String(header.key)}
                      align={align}
                      sx={{
                        fontSize: TABLE_BODY_FONT_SIZE,
                        width: header.width,
                        ...(item.isParent && { py: 10 }),
                        ...(isDisabledClickColumn && { cursor: "default" }),
                      }}
                      onClick={
                        isDisabledClickColumn
                          ? (e) => e.stopPropagation()
                          : undefined
                      }
                    >
                      {value}
                    </TableCell>
                  );
                })}
              </TableRow>
            ))}
          </TableBody>
        </Table>
        {hasChecked && (
          <Box
            position="fixed"
            bottom={16}
            left={0}
            width="100%"
            display="flex"
            justifyContent="center"
            gap={30}
            zIndex={1000}
          >
            <Button
              variant="contained"
              color="primary"
              sx={{ bgcolor: "#2b2b4b", color: "#fff", px: 4 }}
              onClick={handleExpiryDatePrintAllAtOnce}
              disabled={isNotPrintExpiryDate()}
            >
              まとめて賞味期限印刷
            </Button>
            <Button
              variant="contained"
              color="primary"
              sx={{ bgcolor: "#2b2b4b", color: "#fff", px: 4 }}
              onClick={handlePrintAllAtOnce}
            >
              まとめてラベル印刷
            </Button>
          </Box>
        )}
      </TableContainer>
    </>
  );
};

export default TrustBoxTable;
